/**
 * Library of Congress and Dewey call numbers: parsing them into parts and putting
 * them in shelf order.
 *
 * Shelf order is not alphabetical order. The rules, as library shelving guides teach
 * them:
 *
 * - **LC.** Class letters alphabetically (Q, QA, QB … R); the class number as a whole
 *   number (QA9 < QA76 < QA100), then its decimal digit by digit (76.15 < 76.2); each
 *   Cutter by its letter, then its digits as a decimal (.E3 < .E35 < .E359 < .E4);
 *   dates in order; volumes and copies by number.
 * - **Dewey.** The three-digit class number as a whole number, the decimal digit by
 *   digit (641.5 < 641.555 < 641.596 < 641.65), then the book mark alphabetically
 *   (SCH, or a Cutter–Sanborn number such as S386 read like an LC Cutter).
 * - **Both.** Nothing comes before something: QA76.73 .J38 files before
 *   QA76.73 .J38 S53, and a call number without a date before the same one with a date.
 *   Where a date and a Cutter meet at the same position, the date (a number) files
 *   first.
 *
 * Location prefixes ("Ref.", "Folio", "J", "FIC") are kept and grouped first, since a
 * library shelves those collections separately.
 */

export type CallNumberScheme = "lc" | "dewey";

export type CallPart =
  | { kind: "date"; text: string; year: number; suffix: string }
  | {
      kind: "cutter";
      text: string;
      letters: string;
      digits: string;
      suffix: string;
    }
  | { kind: "designation"; text: string; label: string; number: number }
  | { kind: "text"; text: string };

export interface ParsedCallNumber {
  scheme: CallNumberScheme;
  raw: string;
  /** "Ref.", "J", "FIC" — or "". */
  prefix: string;
  /** LC class letters ("QA"); always "" for Dewey. */
  classLetters: string;
  /** "76" in LC, "641" in Dewey; "" for a Dewey line with no number (FIC SMI). */
  classWhole: string;
  /** Digits after the class number's decimal point: "73", "5945". */
  classDecimal: string;
  parts: CallPart[];
}

export type CallNumberParse =
  | { ok: true; value: ParsedCallNumber }
  | { ok: false; raw: string };

const RE_SLASH = /\//g;
const RE_SPACES = /\s+/;
const RE_LC_HEAD =
  /^((?:[A-Za-z][A-Za-z.]*\s+)*)([A-Za-z]{1,3})\s*(\d{1,4})(?:\.(\d+))?(?=$|[\s.A-Za-z])(.*)$/;
const RE_DEWEY_HEAD =
  /^((?:[A-Za-z][A-Za-z.]*\s+)*)(\d{3})(?:\.(\d+))?(?=$|[\s.A-Za-z])(.*)$/;
const RE_COLLECTION_CODE = /^[A-Z][A-Z.]{0,4}$/;
const RE_BOOK_MARK = /^[A-Z][A-Za-z]{0,5}\d*[a-z]?$/;
const RE_DESIGNATION =
  /^(v|vol|c|cop|copy|pt|no|bd|t|ser|suppl|sup|ed|disc|disk|reel|box|fasc|sect|yr)\.\s*(\d+)/i;
const RE_DESIGNATION_LABEL_ONLY =
  /^(v|vol|c|cop|copy|pt|no|bd|t|ser|suppl|sup|ed|disc|disk|reel|box|fasc|sect|yr)\.$/i;
const RE_DATE = /^(\d{4})([a-z]?)(?:[-/]\d{2,4})?$/i;
const RE_ORDINAL = /^(\d+)(?:st|nd|rd|th)$/i;
const RE_NUMBER = /^\d+$/;
// A Cutter is a letter and digits, maybe with a lower-case work mark ("S53a"); two
// can run together ("J38S53"). A work-mark letter is never followed by a digit.
const RE_LC_CUTTERS = /^(?:[A-Za-z]\d+(?:[a-z](?!\d))*)+$/;
const RE_LC_CUTTER = /([A-Za-z])(\d+)((?:[a-z](?!\d))*)/g;
const RE_DEWEY_MARK = /^([A-Za-z]+)(\d*)([a-z]*)$/;
const RE_LEADING_PERIOD = /^\./;
const RE_PERIOD_CUTTER = /(?:^|[\s\d])\.[A-Za-z]/;
const RE_DIGITS_ONLY = /^\d/;

const DESIGNATION_LABELS: Record<string, string> = {
  v: "v",
  vol: "v",
  c: "c",
  cop: "c",
  copy: "c",
  sup: "suppl",
};

/** Split the tail into tokens, joining "v." "2" back into "v.2". */
function tailTokens(rest: string): string[] {
  const raw = rest.trim().split(RE_SPACES).filter(Boolean);
  const tokens: string[] = [];
  for (const token of raw) {
    const previous = tokens.at(-1);
    if (
      previous &&
      RE_DESIGNATION_LABEL_ONLY.test(previous) &&
      RE_NUMBER.test(token)
    ) {
      tokens[tokens.length - 1] = `${previous}${token}`;
    } else {
      tokens.push(token);
    }
  }
  return tokens;
}

function commonPart(token: string): CallPart | null {
  const designation = RE_DESIGNATION.exec(token);
  if (designation) {
    const label = designation[1].toLowerCase();
    return {
      kind: "designation",
      text: token,
      label: DESIGNATION_LABELS[label] ?? label,
      number: Number(designation[2]),
    };
  }
  const date = RE_DATE.exec(token);
  if (date) {
    return {
      kind: "date",
      text: token,
      year: Number(date[1]),
      suffix: date[2].toLowerCase(),
    };
  }
  const ordinal = RE_ORDINAL.exec(token) ?? RE_NUMBER.exec(token);
  if (ordinal) {
    return {
      kind: "designation",
      text: token,
      label: "",
      number: Number(ordinal[1] ?? ordinal[0]),
    };
  }
  return null;
}

function lcTokenParts(token: string): CallPart[] {
  const common = commonPart(token);
  if (common) {
    return [common];
  }
  const bare = token.replace(RE_LEADING_PERIOD, "");
  if (RE_LC_CUTTERS.test(bare)) {
    return [...bare.matchAll(RE_LC_CUTTER)].map((match) => ({
      kind: "cutter" as const,
      text: match[0],
      letters: match[1].toUpperCase(),
      digits: match[2],
      suffix: match[3],
    }));
  }
  return [{ kind: "text", text: token }];
}

function deweyTokenPart(token: string): CallPart {
  const common = commonPart(token);
  if (common) {
    return common;
  }
  const bare = token.replace(RE_LEADING_PERIOD, "");
  const mark = RE_DEWEY_MARK.exec(bare);
  if (mark) {
    return {
      kind: "cutter",
      text: token,
      letters: mark[1].toUpperCase(),
      digits: mark[2],
      suffix: mark[3],
    };
  }
  return { kind: "text", text: token };
}

function isBookMarkPart(token: string): boolean {
  return RE_BOOK_MARK.test(token) || commonPart(token) !== null;
}

function normalizeLine(raw: string): string {
  return raw.replace(RE_SLASH, " ").trim();
}

export function parseLcCallNumber(raw: string): CallNumberParse {
  const line = normalizeLine(raw);
  const head = RE_LC_HEAD.exec(line);
  if (!head || head[3].startsWith("0")) {
    return { ok: false, raw };
  }
  const [, prefix, letters, whole, decimal = "", rest] = head;
  return {
    ok: true,
    value: {
      scheme: "lc",
      raw,
      prefix: prefix.trim(),
      classLetters: letters.toUpperCase(),
      classWhole: whole,
      classDecimal: decimal,
      parts: tailTokens(rest).flatMap(lcTokenParts),
    },
  };
}

export function parseDeweyCallNumber(raw: string): CallNumberParse {
  const line = normalizeLine(raw);
  const head = RE_DEWEY_HEAD.exec(line);
  if (head) {
    const [, prefix, whole, decimal = "", rest] = head;
    return {
      ok: true,
      value: {
        scheme: "dewey",
        raw,
        prefix: prefix.trim(),
        classLetters: "",
        classWhole: whole,
        classDecimal: decimal,
        parts: tailTokens(rest).map(deweyTokenPart),
      },
    };
  }
  // A collection with no class number, shelved by author: "FIC SMI", "B LIN". The
  // prefix is a short capitalised code and every other part must look like a book mark,
  // a date or a volume — so a stray sentence is not mistaken for one.
  const tokens = tailTokens(line);
  if (
    tokens.length >= 2 &&
    tokens.length <= 4 &&
    RE_COLLECTION_CODE.test(tokens[0]) &&
    tokens.slice(1).every(isBookMarkPart)
  ) {
    return {
      ok: true,
      value: {
        scheme: "dewey",
        raw,
        prefix: tokens[0],
        classLetters: "",
        classWhole: "",
        classDecimal: "",
        parts: tokens.slice(1).map(deweyTokenPart),
      },
    };
  }
  return { ok: false, raw };
}

export function parseCallNumber(
  raw: string,
  scheme: CallNumberScheme
): CallNumberParse {
  return scheme === "lc" ? parseLcCallNumber(raw) : parseDeweyCallNumber(raw);
}

/**
 * Which scheme a list is in, by majority. A line reads as Dewey when it starts with a
 * three-digit class number (after any prefix) and has no LC-style ".A12" Cutter.
 */
export function detectScheme(lines: readonly string[]): CallNumberScheme {
  let dewey = 0;
  let lc = 0;
  for (const raw of lines) {
    const line = normalizeLine(raw);
    const deweyLike =
      RE_DIGITS_ONLY.test(line) ||
      (RE_DEWEY_HEAD.test(line) && !RE_PERIOD_CUTTER.test(line));
    if (deweyLike) {
      dewey += 1;
    } else if (parseLcCallNumber(line).ok) {
      lc += 1;
    }
  }
  return dewey > lc ? "dewey" : "lc";
}

/* ------------------------------------------------------------- comparison */

const RE_PREFIX_NOISE = /[.\s]/g;

/** Plain code-unit order on upper case: deterministic, and digits before letters. */
function compareText(a: string, b: string): number {
  const left = a.toUpperCase();
  const right = b.toUpperCase();
  if (left === right) {
    return 0;
  }
  return left < right ? -1 : 1;
}

/** Whole numbers numerically; an absent number files first. */
function compareWhole(a: string, b: string): number {
  if (a === b) {
    return 0;
  }
  if (a === "" || b === "") {
    return a === "" ? -1 : 1;
  }
  return Number(a) - Number(b);
}

/**
 * Decimal digits compared digit by digit, shorter first on a tie — which is plain
 * string order on digit strings: "15" < "2" because .15 < .2, "2" < "25".
 */
function compareDecimal(a: string, b: string): number {
  if (a === b) {
    return 0;
  }
  return a < b ? -1 : 1;
}

const KIND_ORDER: Record<CallPart["kind"], number> = {
  date: 0,
  cutter: 1,
  designation: 2,
  text: 3,
};

function comparePart(a: CallPart, b: CallPart): number {
  if (a.kind !== b.kind) {
    return KIND_ORDER[a.kind] - KIND_ORDER[b.kind];
  }
  if (a.kind === "date" && b.kind === "date") {
    return a.year - b.year || compareText(a.suffix, b.suffix);
  }
  if (a.kind === "cutter" && b.kind === "cutter") {
    return (
      compareText(a.letters, b.letters) ||
      compareDecimal(a.digits, b.digits) ||
      compareText(a.suffix, b.suffix)
    );
  }
  if (a.kind === "designation" && b.kind === "designation") {
    return compareText(a.label, b.label) || a.number - b.number;
  }
  return compareText(a.text, b.text);
}

function compareParts(a: readonly CallPart[], b: readonly CallPart[]): number {
  const length = Math.min(a.length, b.length);
  for (let index = 0; index < length; index++) {
    const order = comparePart(a[index], b[index]);
    if (order !== 0) {
      return order;
    }
  }
  return a.length - b.length;
}

export function compareCallNumbers(
  a: ParsedCallNumber,
  b: ParsedCallNumber
): number {
  return (
    compareText(
      a.prefix.replace(RE_PREFIX_NOISE, ""),
      b.prefix.replace(RE_PREFIX_NOISE, "")
    ) ||
    compareText(a.classLetters, b.classLetters) ||
    compareWhole(a.classWhole, b.classWhole) ||
    compareDecimal(a.classDecimal, b.classDecimal) ||
    compareParts(a.parts, b.parts)
  );
}

export interface ShelfOrder {
  scheme: CallNumberScheme;
  sorted: ParsedCallNumber[];
  /** Lines that are not call numbers in the scheme, in the order they were pasted. */
  unreadable: string[];
}

/** Parse every line in one scheme (detected unless given) and sort the readable ones. */
export function shelfOrder(
  lines: readonly string[],
  scheme: CallNumberScheme | "auto" = "auto"
): ShelfOrder {
  const resolved = scheme === "auto" ? detectScheme(lines) : scheme;
  const sorted: ParsedCallNumber[] = [];
  const unreadable: string[] = [];
  for (const line of lines) {
    const parsed = parseCallNumber(line, resolved);
    if (parsed.ok) {
      sorted.push(parsed.value);
    } else {
      unreadable.push(line);
    }
  }
  sorted.sort(compareCallNumbers);
  return { scheme: resolved, sorted, unreadable };
}
